/**
 * Phase 4 — Rate Card Excel utilities (frontend only, additive).
 * Uses SheetJS (xlsx).
 *
 * Sheets:
 *   1. FM_LM_Rates       — FM & LM rows × 4 rate columns
 *   2. MM_Matrix         — 7×7 distance bands × weight slabs
 *   3. B2B_Zone_Matrix   — 9×9 (read-only on upload)
 *   4. B2C_Slabs         — weight slab × zone columns (read-only on upload)
 *   5. ODA_Pincodes      — pincodes with surcharge info
 */
import * as XLSX from "xlsx";

export const MM_BANDS = ["0-50", "50-200", "200-400", "400-900", "900-1500", "1500-2500", "2500+"];
export const MM_SLABS = ["0-20", "20-50", "50-100", "100-200", "200-300", "300-500", "500-1000"];
export const B2B_REGIONS = ["S1", "N1", "N2", "E", "NE", "W1", "W2", "S2", "CTR"];
export const B2C_ZONES = ["A", "B", "C1", "C2", "D1", "D2", "E", "F"];
export const B2C_SLABS = ["0-0.5", "0.5-1", "1-2", "2-5", "5-10"];

export const FM_LM_FIELDS = ["rate_per_kg", "free_km", "rate_per_km_beyond", "min_charge"];

/* ─────────── helpers ─────────── */
function aoaToSheet(rows) {
  return XLSX.utils.aoa_to_sheet(rows);
}

function ddmmyyyy(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}${pad(d.getMonth() + 1)}${d.getFullYear()}`;
}

/* ─────────── build workbook ─────────── */
export function buildRateCardWorkbook({ fm, lm, mmGrid, b2bMatrix, b2cRates, odaPincodes }) {
  const wb = XLSX.utils.book_new();

  // Sheet 1 — FM/LM
  const s1Header = ["component", ...FM_LM_FIELDS];
  const s1 = [
    s1Header,
    ["FM", ...FM_LM_FIELDS.map((k) => fm?.[k] ?? 0)],
    ["LM", ...FM_LM_FIELDS.map((k) => lm?.[k] ?? 0)],
  ];
  XLSX.utils.book_append_sheet(wb, aoaToSheet(s1), "FM_LM_Rates");

  // Sheet 2 — MM Matrix
  const s2 = [
    ["band\\slab", ...MM_SLABS],
    ...MM_BANDS.map((band) => [band, ...MM_SLABS.map((slab) => mmGrid?.[band]?.[slab] ?? 0)]),
  ];
  XLSX.utils.book_append_sheet(wb, aoaToSheet(s2), "MM_Matrix");

  // Sheet 3 — B2B
  const s3 = [
    ["origin\\dest", ...B2B_REGIONS],
    ...B2B_REGIONS.map((row) => [row, ...B2B_REGIONS.map((col) => b2bMatrix?.[row]?.[col] ?? 0)]),
  ];
  XLSX.utils.book_append_sheet(wb, aoaToSheet(s3), "B2B_Zone_Matrix");

  // Sheet 4 — B2C Slabs
  const s4 = [
    ["slab(kg)\\zone", ...B2C_ZONES],
    ...B2C_SLABS.map((slab) => [slab, ...B2C_ZONES.map((z) => b2cRates?.[slab]?.[z] ?? "")]),
  ];
  XLSX.utils.book_append_sheet(wb, aoaToSheet(s4), "B2C_Slabs");

  // Sheet 5 — ODA pincodes
  const s5 = [
    ["pincode", "city", "state", "type", "surcharge_per_kg", "min_surcharge", "is_manual_override", "is_active"],
    ...(odaPincodes || []).map((p) => [
      p.pincode, p.city || "", p.state || "", p.type || "ODA",
      p.surcharge_per_kg ?? 0, p.min_surcharge ?? 0,
      p.is_manual_override ? "YES" : "NO", p.is_active === false ? "NO" : "YES",
    ]),
  ];
  XLSX.utils.book_append_sheet(wb, aoaToSheet(s5), "ODA_Pincodes");

  return wb;
}

export function downloadRateCard(wb, filename = `Sendtoo_Rate_Card_${ddmmyyyy()}.xlsx`) {
  XLSX.writeFile(wb, filename);
}

/* ─────────── parse + validate ─────────── */
export async function parseRateCardFile(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const required = ["FM_LM_Rates", "MM_Matrix", "B2B_Zone_Matrix", "B2C_Slabs", "ODA_Pincodes"];
  const errors = [];
  for (const name of required) {
    if (!wb.SheetNames.includes(name)) {
      errors.push({ sheet: name, cell: "—", error: `Missing sheet: ${name}` });
    }
  }
  if (errors.length) return { valid: false, errors };

  const toAoa = (name) => XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: "" });

  // ── Sheet 1: FM/LM
  const s1 = toAoa("FM_LM_Rates");
  const parsed = { fm: {}, lm: {}, mmGrid: {}, b2bMatrix: {}, b2cRates: {}, odaPincodes: [] };
  if (!s1[0] || s1[0][0] !== "component") {
    errors.push({ sheet: "FM_LM_Rates", cell: "A1", error: "Header row missing 'component'" });
  } else {
    const headerKeys = s1[0].slice(1);
    for (const k of FM_LM_FIELDS) {
      if (!headerKeys.includes(k)) errors.push({ sheet: "FM_LM_Rates", cell: "—", error: `Missing column: ${k}` });
    }
    for (let i = 1; i < s1.length; i++) {
      const row = s1[i];
      const tag = String(row[0] || "").toUpperCase();
      if (tag !== "FM" && tag !== "LM") continue;
      const target = tag === "FM" ? parsed.fm : parsed.lm;
      headerKeys.forEach((k, idx) => {
        const v = Number(row[idx + 1]);
        if (Number.isNaN(v) || v < 0) {
          errors.push({ sheet: "FM_LM_Rates", cell: `${String.fromCharCode(66 + idx)}${i + 1}`, error: `${tag} ${k} must be a positive number` });
        }
        target[k] = v;
      });
    }
  }

  // ── Sheet 2: MM Matrix
  const s2 = toAoa("MM_Matrix");
  if (!s2[0] || s2[0].length !== MM_SLABS.length + 1) {
    errors.push({ sheet: "MM_Matrix", cell: "row 1", error: `Expected ${MM_SLABS.length} slab columns + header` });
  } else {
    const slabHdr = s2[0].slice(1).map(String);
    for (let i = 1; i <= MM_BANDS.length; i++) {
      const row = s2[i];
      if (!row) {
        errors.push({ sheet: "MM_Matrix", cell: `row ${i + 1}`, error: "Missing band row" });
        continue;
      }
      const band = String(row[0]);
      if (!MM_BANDS.includes(band)) {
        errors.push({ sheet: "MM_Matrix", cell: `A${i + 1}`, error: `Unknown band '${band}'` });
        continue;
      }
      parsed.mmGrid[band] = {};
      for (let j = 0; j < MM_SLABS.length; j++) {
        const slab = slabHdr[j];
        const v = Number(row[j + 1]);
        if (Number.isNaN(v) || v < 0) {
          errors.push({ sheet: "MM_Matrix", cell: `${String.fromCharCode(66 + j)}${i + 1}`, error: `Cell must be number` });
        }
        parsed.mmGrid[band][slab] = v;
      }
    }
  }

  // ── Sheet 3: B2B (read-only)
  const s3 = toAoa("B2B_Zone_Matrix");
  if (s3[0]) {
    const cols = s3[0].slice(1).map(String);
    for (const r of B2B_REGIONS) {
      if (!cols.includes(r)) errors.push({ sheet: "B2B_Zone_Matrix", cell: "row 1", error: `Missing region column ${r}` });
    }
    for (let i = 1; i < s3.length; i++) {
      const row = s3[i];
      if (!row || !row[0]) continue;
      const origin = String(row[0]);
      parsed.b2bMatrix[origin] = {};
      cols.forEach((c, idx) => {
        parsed.b2bMatrix[origin][c] = Number(row[idx + 1]) || 0;
      });
    }
  }

  // ── Sheet 4: B2C (read-only)
  const s4 = toAoa("B2C_Slabs");
  if (s4[0]) {
    const cols = s4[0].slice(1).map(String);
    for (const z of B2C_ZONES) {
      if (!cols.includes(z)) errors.push({ sheet: "B2C_Slabs", cell: "row 1", error: `Missing zone column ${z}` });
    }
    for (let i = 1; i < s4.length; i++) {
      const row = s4[i];
      if (!row || !row[0]) continue;
      const slab = String(row[0]);
      parsed.b2cRates[slab] = {};
      cols.forEach((c, idx) => {
        parsed.b2cRates[slab][c] = Number(row[idx + 1]) || 0;
      });
    }
  }

  // ── Sheet 5: ODA pincodes
  const s5 = toAoa("ODA_Pincodes");
  if (s5.length > 1) {
    for (let i = 1; i < s5.length; i++) {
      const row = s5[i];
      if (!row || !row[0]) continue;
      const pin = String(row[0]).trim();
      if (!/^\d{6}$/.test(pin)) {
        errors.push({ sheet: "ODA_Pincodes", cell: `A${i + 1}`, error: `Pincode '${pin}' must be 6 digits` });
        continue;
      }
      parsed.odaPincodes.push({
        pincode: pin,
        city: row[1] || "",
        state: row[2] || "",
        type: row[3] || "ODA",
        surcharge_per_kg: Number(row[4]) || 0,
        min_surcharge: Number(row[5]) || 0,
        is_manual_override: String(row[6] || "").toUpperCase() === "YES",
        is_active: String(row[7] || "YES").toUpperCase() !== "NO",
      });
    }
  }

  return { valid: errors.length === 0, errors, parsed };
}

/* ─────────── diff helpers ─────────── */
export function diffFmLm(current, incoming) {
  const out = [];
  for (const tag of ["fm", "lm"]) {
    for (const k of FM_LM_FIELDS) {
      const cur = current?.[tag]?.[k];
      const inc = incoming?.[tag]?.[k];
      out.push({
        field: `${tag.toUpperCase()} ${k}`,
        current: cur ?? 0,
        incoming: inc ?? 0,
        changed: Number(cur) !== Number(inc),
      });
    }
  }
  return out;
}

export function diffMm(currentGrid, incomingGrid) {
  const out = [];
  for (const band of MM_BANDS) {
    for (const slab of MM_SLABS) {
      const cur = currentGrid?.[band]?.[slab] ?? 0;
      const inc = incomingGrid?.[band]?.[slab] ?? 0;
      out.push({
        field: `${band} / ${slab}`,
        band, slab,
        current: cur,
        incoming: inc,
        changed: Number(cur) !== Number(inc),
      });
    }
  }
  return out;
}

export function diffOda(currentList, incomingList) {
  const curMap = Object.fromEntries((currentList || []).map((p) => [p.pincode, p]));
  return (incomingList || []).map((p) => {
    const cur = curMap[p.pincode];
    return {
      field: p.pincode,
      current: cur ? `${cur.surcharge_per_kg}/${cur.min_surcharge}` : "—",
      incoming: `${p.surcharge_per_kg}/${p.min_surcharge}`,
      changed: !cur
        || Number(cur.surcharge_per_kg) !== Number(p.surcharge_per_kg)
        || Number(cur.min_surcharge) !== Number(p.min_surcharge)
        || !!cur.is_active !== !!p.is_active,
    };
  });
}
